import React, { ReactElement } from 'react'

import { useDebouncedCallback } from 'use-debounce'
import styled from 'styled-components'

import { variables } from 'src/Styled/variables'
import Loader from 'src/Assets/Svg/loader.svg?react'
import { DEBOUNCE_DELAY_100 } from 'src/Constants/constants'
import { secondaryButtonStyles } from 'src/Components/Buttons/PrimaryButton'

export interface GreyTextButtonProps extends React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> {
  icon?: React.ReactNode
  loading?: boolean
}

export const StyledButton = styled.button<{
  $loading?: boolean
}>`
  position: relative;
  display: flex;
  height: 48px;
  padding: 8px 40px;
  justify-content: center;
  align-items: center;
  border-radius: 40px;
  gap: 8px;
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;

  ${secondaryButtonStyles}
`

const LoaderWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  svg {
    width: 17px;
    height: 17px;
  }
`

const ChildrenWrapper = styled.div<{ $loading?: boolean }>`
  opacity: ${({ $loading }) => ($loading ? 0 : 1)};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  background: inherit;
`

export const GreyTextButton: React.FC<GreyTextButtonProps> = (
  props,
): ReactElement => {
  const { children, loading, onClick, disabled, icon, ...restProps } = props

  const debouncedOnClick = useDebouncedCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (typeof onClick === 'function') {
        onClick(event)
      }
    },
    DEBOUNCE_DELAY_100,
  )
  return (
    <StyledButton
      {...restProps}
      $loading={loading && !disabled}
      onClick={(e) => debouncedOnClick(e)}
      disabled={disabled || loading}
    >
      {loading && !disabled && (
        <LoaderWrapper>
          <Loader data-testid='loader' />
        </LoaderWrapper>
      )}
      {Boolean(icon) && !loading && icon}
      <ChildrenWrapper $loading={loading}>{children}</ChildrenWrapper>
    </StyledButton>
  )
}
